import { useCallback, useMemo, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { FilterAltRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { PoGinesysSyncBadge } from "../CreatePurchaseOrder/poUi";
import FilterModal from "../AllPurchaseOrders/FilterModal/FilterModal";
import {
	getPoSyncLogFilterColumns,
	listPurchaseOrderSyncLogsApi,
	lsPoSyncLogFilterValStr,
	poSyncLogEventTone,
	poSyncLogTableColumns,
} from "./data";
import SyncLogDetailModal from "./SyncLogDetailModal";

const PoSyncEventBadge = ({ value }) => {
	const key = value || "QUEUED";
	const tone = poSyncLogEventTone[key] || poSyncLogEventTone.QUEUED;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1.1,
				py: 0.35,
				borderRadius: "8px",
				fontSize: 12,
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: tone.color,
				bgcolor: tone.bgcolor,
				width: "fit-content",
			}}>
			{key}
		</Box>
	);
};

const PoSyncLogs = () => {
	const { setLoadingOpen, snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const [data, setData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterModal, setFilterModal] = useState(false);
	const [detailRow, setDetailRow] = useState(null);
	const [gridRefreshKey, setGridRefreshKey] = useState(0);
	const [filterColumns, setFilterColumns] = useState(() => {
		if (!localStorage.getItem(lsPoSyncLogFilterValStr)) return [];
		const localStorageObj = JSON.parse(localStorage.getItem(lsPoSyncLogFilterValStr));
		if (typeof localStorageObj !== "object" || !localStorageObj) return [];
		return getPoSyncLogFilterColumns().filter((row) => localStorageObj[row.key] !== undefined);
	});
	const [filterColumnVal, setFilterColumnVal] = useState(() => {
		if (localStorage.getItem(lsPoSyncLogFilterValStr)) {
			return JSON.parse(localStorage.getItem(lsPoSyncLogFilterValStr));
		}
		return {};
	});

	const filterColumnOptions = useMemo(() => getPoSyncLogFilterColumns(), []);

	const tableColumns = useMemo(
		() =>
			poSyncLogTableColumns.map((col) => {
				if (col.key === "event_type") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<PoSyncEventBadge value={row.event_type} />
							</Box>
						),
					};
				}
				if (col.key === "ginesys_sync") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<PoGinesysSyncBadge value={row.ginesys_sync} />
							</Box>
						),
					};
				}
				if (col.key === "created_at") {
					return {
						...col,
						valueGetter: ({ row }) => {
							if (!row.created_at) return "—";
							const d = new Date(row.created_at);
							return Number.isNaN(d.getTime()) ? String(row.created_at) : d.toLocaleString();
						},
					};
				}
				if (col.key === "error_message") {
					return {
						...col,
						valueGetter: ({ row }) => row.error_message || "—",
					};
				}
				return col;
			}),
		[]
	);

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			const isFirstLoad = data.length === 0;
			if (isFirstLoad) setLoadingOpen(true);
			try {
				const result = await listPurchaseOrderSyncLogsApi({ limit, offset, filter });
				return {
					rows: result.rows || [],
					total_rows_count: result.total_rows_count ?? 0,
				};
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: parseErrorMessage(error),
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				return { rows: [], total_rows_count: 0 };
			} finally {
				if (isFirstLoad) setLoadingOpen(false);
			}
		},
		[data.length, setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar]
	);

	const handleRowClick = ({ row }) => {
		if (!row) return;
		setDetailRow(row);
	};

	return (
		<Box
			sx={{
				height: "calc(100vh - 135px)",
				p: 1,
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				gap: 1,
			}}>
			<Box display="flex" columnGap={1} pt={0.5} flexWrap="wrap" alignItems="center" rowGap="5px">
				<Button
					variant="contained"
					size="small"
					startIcon={<FilterAltRounded />}
					onClick={() => setFilterModal(true)}
					sx={{ textTransform: "capitalize" }}>
					Filter
				</Button>
				:
				{!filterColumns?.length && (
					<Typography variant="body1" fontSize={16} color={grey[700]}>
						None
					</Typography>
				)}
				{filterColumns?.map((row) => (
					<Chip
						key={row.key}
						label={`${row.name} : ${
							Array.isArray(filterColumnVal[row.key])
								? filterColumnVal[row.key]?.join(", ")
								: filterColumnVal?.[row.key]
						}`}
						size="small"
					/>
				))}
			</Box>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<TanstackDatagrid
					key={gridRefreshKey}
					columns={tableColumns}
					pageData={data}
					setPageData={setData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					filterObj={filterColumnVal}
					setFilterObj={setFilterColumnVal}
					disableColumnFilter
					disableColumnSort
					disablePagination
					isLoading={false}
					emptyMessage="No PO sync logs found"
					handleRowClick={handleRowClick}
					getServerSideData={fetchData}
					getRowId={(row) => row.id}>
					<Box sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}>
						<Typography variant="body2" color="text.secondary">
							{rowCount ? `${rowCount} sync log${rowCount === 1 ? "" : "s"}` : "PO sync logs"}
						</Typography>
					</Box>
				</TanstackDatagrid>
			</Box>

			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={() => setFilterModal(false)}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnVal}
					localStorageStr={lsPoSyncLogFilterValStr}
					allColumns={filterColumnOptions}
				/>
			)}

			<SyncLogDetailModal
				open={Boolean(detailRow)}
				onClose={() => setDetailRow(null)}
				row={detailRow}
				onActionComplete={() => {
					setGridRefreshKey((k) => k + 1);
					setDetailRow(null);
				}}
			/>
		</Box>
	);
};

export default PoSyncLogs;
